import { useRef, useState } from 'react';
import { Banner, Button, Field, PasswordInput, TextInput } from '@/components/ui';
import { GateCard } from '@/app/shell/Gates';
import { useSession } from '@/auth/session';
import { messageOf } from '@/api/errors';
import { AppleLogo } from './AppleLogo';
import { authMessage } from './authMessage';
import googleG from '@/assets/google_g_logo.png';
import s from './SignInPage.module.css';

type Mode = 'choose' | 'email' | 'create' | 'reset';

export function SignInPage() {
  const { status, error: sessionError, retry, devMode } = useSession();
  const [mode, setMode] = useState<Mode>('choose');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Only the latest attempt may clear `busy` or set the error: a second click cancels the first
  // popup, and that one's rejection must not hand the buttons back mid-way through the second.
  const attempt = useRef(0);

  if (status === 'unconfigured') {
    return (
      <GateCard>
        <h1 className={s.title}>Sign-in is not configured</h1>
        <p className={s.body}>
          This build has no Firebase web app configuration. Set the <code>VITE_FIREBASE_*</code> variables, or{' '}
          <code>VITE_DEV_AUTH_TOKEN</code> for local development (see <code>web/.env.example</code>).
        </p>
      </GateCard>
    );
  }

  // Local development signs in with the dev token, not Firebase, so after a sign-out the provider
  // buttons would have nothing to talk to. Signing back in is the same exchange the page load does.
  if (devMode) {
    return (
      <GateCard>
        <div className={s.head}>
          <h1 className={s.title}>Signed out</h1>
          <p className={s.body}>
            Local development signs in with <code>VITE_DEV_AUTH_TOKEN</code> as the API's dev user, not with Firebase.
          </p>
        </div>
        {sessionError != null && <Banner tone="neg">{messageOf(sessionError)}</Banner>}
        <Button variant="primary" size="lg" block icon="login" onClick={() => void retry()}>
          Sign in as the dev user
        </Button>
      </GateCard>
    );
  }

  const run = async (fn: () => Promise<unknown>, { popup = false }: { popup?: boolean } = {}) => {
    const id = ++attempt.current;
    setBusy(true);
    setError('');
    setNotice('');
    // Firebase notices a closed popup only by polling for it (every 2 s, 10 s on a phone) and then
    // waiting 8 s for a late result before rejecting, so the buttons sat disabled for 10–18 s after
    // the person closed it, and the page read as frozen. Focus returning to this page is the popup
    // closing: allow a moment for a result, then hand the buttons back. A sign-in that still
    // completes lands through the session's own listener, not through this promise.
    let release = () => {};
    if (popup) {
      let timer: number | undefined;
      const onFocus = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          if (attempt.current === id) setBusy(false);
        }, 1000);
      };
      window.addEventListener('focus', onFocus);
      release = () => {
        window.removeEventListener('focus', onFocus);
        window.clearTimeout(timer);
      };
    }
    try {
      await fn();
    } catch (e) {
      if (attempt.current === id) setError(authMessage(e));
    } finally {
      release();
      if (attempt.current === id) setBusy(false);
    }
  };

  const fb = () => import('@/auth/firebase');

  return (
    <GateCard>
      <div className={s.head}>
        <h1 className={s.title}>{mode === 'create' ? 'Create your account' : mode === 'reset' ? 'Reset your password' : 'Sign in'}</h1>
        <p className={s.body}>Your strategies, alerts and test results — the same account as the phone app.</p>
      </div>
      {sessionError != null && !error && (
        <Banner tone="neg">
          <span className={s.retryRow}>
            <span>Signed in, but the server could not be reached: {messageOf(sessionError)}</span>
            <Button size="sm" onClick={() => run(retry)}>
              Try again
            </Button>
          </span>
        </Banner>
      )}
      {error && <Banner tone="neg">{error}</Banner>}
      {notice && <Banner tone="pos">{notice}</Banner>}

      {mode === 'choose' && (
        <div className={s.stack}>
          <button type="button" className={s.provider} disabled={busy} onClick={() => run(async () => (await fb()).signInWithGoogle(), { popup: true })}>
            <img src={googleG} alt="" width={18} height={18} />
            Continue with Google
          </button>
          <button type="button" className={s.provider} disabled={busy} onClick={() => run(async () => (await fb()).signInWithApple(), { popup: true })}>
            <AppleLogo />
            Continue with Apple
          </button>
          <div className={s.or}>
            <span>or</span>
          </div>
          <Button size="lg" block icon="mail" onClick={() => setMode('email')}>
            Continue with email
          </Button>
        </div>
      )}

      {(mode === 'email' || mode === 'create') && (
        <form
          className={s.stack}
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              const f = await fb();
              if (mode === 'create') {
                // The session's exchange answers `email_not_verified`, and the root layout replaces
                // this page with the verification step (Gates.tsx `VerifyEmailStep`).
                await f.createEmailAccount(email, password);
              } else {
                await f.signInWithEmail(email, password);
              }
            });
          }}
        >
          <Field label="Email">{(id) => <TextInput id={id} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />}</Field>
          <Field label="Password" hint={mode === 'create' ? 'At least 8 characters.' : undefined}>
            {(id) => (
              <PasswordInput
                id={id}
                autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={mode === 'create' ? 8 : undefined}
                required
              />
            )}
          </Field>
          <Button type="submit" variant="primary" size="lg" block loading={busy}>
            {mode === 'create' ? 'Create account' : 'Sign in'}
          </Button>
          <div className={s.links}>
            {mode === 'email' ? (
              <>
                <button type="button" onClick={() => setMode('reset')}>Forgot password?</button>
                <button type="button" onClick={() => setMode('create')}>Create an account</button>
              </>
            ) : (
              <button type="button" onClick={() => setMode('email')}>I already have an account</button>
            )}
            <button type="button" onClick={() => setMode('choose')}>Other ways to sign in</button>
          </div>
        </form>
      )}

      {mode === 'reset' && (
        <form
          className={s.stack}
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await (await fb()).sendPasswordReset(email);
              setNotice('If an account uses that email, a reset link is on its way.');
            });
          }}
        >
          <Field label="Email">{(id) => <TextInput id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />}</Field>
          <Button type="submit" variant="primary" size="lg" block loading={busy}>
            Send reset link
          </Button>
          <div className={s.links}>
            <button type="button" onClick={() => setMode('email')}>Back to sign in</button>
          </div>
        </form>
      )}

      <p className={s.legal}>
        SixOne8 provides monitoring tools only and does not give financial advice. By continuing you agree to the{' '}
        <a href="https://sixone8.app/terms" target="_blank" rel="noopener noreferrer">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="https://sixone8.app/privacy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
        .
      </p>
    </GateCard>
  );
}
